/* ============================================================
   IDEAS CAFE — Product page

   Three small elements rather than one monolith:

     <ideas-gallery>   thumbnail ↔ frame switching
     <ideas-pairings>  fetches complementary products, hides itself
                       when there are none
     (form handler)    variant selection, quantity, async add-to-cart

   Add-to-cart publishes through the shared IdeasCart contract so the
   header count and cart rail update without extra wiring.
   ============================================================ */
(function () {
  'use strict';

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* ── Gallery ───────────────────────────────────────────── */

  if (!customElements.get('ideas-gallery')) {
    class IdeasGallery extends HTMLElement {
      connectedCallback() {
        this.frames = Array.prototype.slice.call(this.querySelectorAll('[data-frame]'));
        this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-goto-media]'));
        if (this.thumbs.length < 2) return;

        var self = this;
        this.thumbs.forEach(function (thumb) {
          thumb.addEventListener('click', function () {
            self.show(thumb.dataset.gotoMedia);
          });
        });
      }

      show(mediaId) {
        if (!mediaId) return;

        this.frames.forEach(function (frame) {
          var on = frame.dataset.mediaId === String(mediaId);
          frame.classList.toggle('is-active', on);
          if (on) frame.removeAttribute('aria-hidden');
          else frame.setAttribute('aria-hidden', 'true');
        });

        this.thumbs.forEach(function (thumb) {
          var on = thumb.dataset.gotoMedia === String(mediaId);
          thumb.classList.toggle('is-active', on);
          thumb.setAttribute('aria-selected', on ? 'true' : 'false');
        });
      }
    }
    customElements.define('ideas-gallery', IdeasGallery);
  }

  /* ── Pairings ──────────────────────────────────────────── */

  if (!customElements.get('ideas-pairings')) {
    class IdeasPairings extends HTMLElement {
      connectedCallback() {
        var url = this.dataset.url;
        var target = this.querySelector('[data-pairings-target]');
        if (!url || !target) return;

        var self = this;
        fetch(url)
          .then(function (res) {
            if (!res.ok) throw new Error('Recommendations failed: ' + res.status);
            return res.text();
          })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var grid = doc.querySelector('[data-pairings-grid]');
            // No configured complementary products — stay hidden
            // rather than show an empty heading.
            if (!grid || !grid.children.length) return;
            target.appendChild(grid);
            self.hidden = false;
            self.bindArrows(grid);
          })
          .catch(function (err) {
            console.warn('[ideas] pairings failed', err);
          });
      }

      /* The arrows live in the product section, outside the fetched
         markup, so they survive the injection — but that also means they
         cannot be wired until the track exists. */
      bindArrows(track) {
        var prev = this.querySelector('[data-pair-prev]');
        var next = this.querySelector('[data-pair-next]');
        if (!prev && !next) return;

        var self = this;

        this.addEventListener('click', function (event) {
          var back = event.target.closest('[data-pair-prev]');
          var fwd = event.target.closest('[data-pair-next]');
          if (!back && !fwd) return;

          event.preventDefault();
          // A card plus its gap, so a press lands on a card edge.
          var card = track.firstElementChild;
          var step = card ? card.offsetWidth + 12 : track.clientWidth;
          track.scrollBy({ left: back ? -step : step, behavior: 'smooth' });
        });

        track.addEventListener('scroll', function () {
          self.updateArrows(track);
        });
        window.addEventListener('resize', function () {
          self.updateArrows(track);
        });

        this.updateArrows(track);
      }

      // Disabled at the end of travel, rather than left as a control
      // that silently does nothing.
      updateArrows(track) {
        var prev = this.querySelector('[data-pair-prev]');
        var next = this.querySelector('[data-pair-next]');
        if (!prev || !next) return;

        var max = track.scrollWidth - track.clientWidth;
        if (max <= 1) {
          prev.disabled = true;
          next.disabled = true;
          return;
        }
        prev.disabled = track.scrollLeft <= 1;
        next.disabled = track.scrollLeft >= max - 1;
      }
    }
    customElements.define('ideas-pairings', IdeasPairings);
  }

  /* ── Buy form ──────────────────────────────────────────── */

  var form = document.getElementById('IdeasProductForm');
  if (!form) return;

  var variantInput = form.querySelector('[data-variant-input]');
  var addButton = form.querySelector('[data-add-button]');
  var addLabel = form.querySelector('[data-add-label]');
  var addPrice = form.querySelector('[data-add-price]');
  var qtyInput = form.querySelector('[data-qty-input]');

  var dataEl = document.querySelector('[data-variant-data]');
  var variants = [];
  try {
    variants = dataEl ? JSON.parse(dataEl.textContent) : [];
  } catch (e) {
    console.warn('[ideas] variant data unreadable', e);
  }

  /* Quantity stepper */

  form.querySelectorAll('[data-qty]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var step = parseInt(btn.dataset.qty, 10) || 0;
      var next = (parseInt(qtyInput.value, 10) || 1) + step;
      // Never below one: zero would mean "remove", which is not what
      // this control does.
      qtyInput.value = Math.max(1, next);
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  /* Variant selection */

  function selectedOptions() {
    return Array.prototype.map.call(
      form.querySelectorAll('[data-option-input]:checked'),
      function (input) {
        return input.value;
      }
    );
  }

  function matchVariant(chosen) {
    return variants.find(function (variant) {
      return chosen.every(function (value, index) {
        return variant.options[index] === value;
      });
    });
  }

  function updateForVariant() {
    if (!variants.length) return;
    var variant = matchVariant(selectedOptions());

    if (!variant) {
      addButton.disabled = true;
      addLabel.textContent = addButton.dataset.labelUnavailable || addLabel.textContent;
      return;
    }

    variantInput.value = variant.id;
    addButton.disabled = !variant.available;

    if (addPrice) {
      // Money formatting is not reimplemented here — the price node is
      // re-fetched below so it stays server-formatted.
      addPrice.textContent = '';
    }

    // Keep the URL shareable: a copied link should land on the same
    // variant. replaceState so the back button is not polluted.
    var url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    refreshPrice(variant.id);
  }

  // Re-render the price and add-button label from the server so PKR
  // formatting matches checkout exactly.
  function refreshPrice(variantId) {
    var section = document.querySelector('[data-section-id]');
    var sectionId = section ? section.dataset.sectionId : null;
    var url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    if (sectionId) url.searchParams.set('section_id', sectionId);

    fetch(url.toString())
      .then(function (res) {
        return res.ok ? res.text() : null;
      })
      .then(function (html) {
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');

        var freshPrice = doc.querySelector('[data-price]');
        var currentPrice = document.querySelector('[data-price]');
        if (freshPrice && currentPrice) currentPrice.innerHTML = freshPrice.innerHTML;

        var freshLabel = doc.querySelector('[data-add-label]');
        if (freshLabel && addLabel) addLabel.innerHTML = freshLabel.innerHTML;
      })
      .catch(function (err) {
        console.warn('[ideas] variant price refresh failed', err);
      });
  }

  form.querySelectorAll('[data-option-input]').forEach(function (input) {
    input.addEventListener('change', updateForVariant);
  });

  /* Line-item properties declared by the page.

     Apps (the add-on picker, special instructions, gift messages) add
     inputs named `properties[Something]`. Deliberately NOT FormData on
     the form: these apps inject at runtime and do not reliably land
     inside the <form> element — several drop their markup next to the
     buy box instead, and FormData would not see those at all. Scanning
     the buy-box panel catches both placements.

     Unchecked boxes, unselected radios and disabled fields are skipped,
     mirroring what a native form submit would send. A repeated name —
     a checkbox group such as "Extra toppings" — is joined, because a
     line-item property holds a single string. */
  function collectProperties() {
    var props = {};
    // Scoped to the whole section, not the form: these apps inject at
    // runtime and place their block wherever they like — above the buy
    // box, after the info table, sometimes as a sibling of the form.
    var scope = form.closest('.shopify-section') || form.closest('.ideas-product__info') || form;
    var fields = scope.querySelectorAll('[name^="properties["]');

    Array.prototype.forEach.call(fields, function (field) {
      if (field.disabled) return;
      if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) return;

      var match = /^properties\[(.+)\]$/.exec(field.name);
      if (!match) return;

      var key = match[1].trim();
      if (!key) return;

      var val;
      if (field.multiple && field.options) {
        val = Array.prototype.filter
          .call(field.options, function (o) {
            return o.selected;
          })
          .map(function (o) {
            return o.value;
          })
          .join(', ');
      } else {
        val = field.value;
      }

      val = (val || '').trim();
      if (!val) return;

      props[key] = props[key] ? props[key] + ', ' + val : val;
    });

    return props;
  }

  /* Neither the branch nor the fulfilment type is attached to the line.

     Both are informational on the product page: they tell the customer
     where we deliver, where they can collect, and how long each takes.
     They are not choices about THIS product, so they are not product
     options and nothing about them is written onto the line.

     The basket-wide choice still reaches the order the way it always
     has — <ideas-order-type> writes it to the cart attributes, which is
     the mechanism the order-type popup and checkout already read. */

  /* Async add to cart */

  form.addEventListener('submit', function (event) {
    event.preventDefault();
    if (addButton.disabled || addButton.dataset.busy === 'true') return;

    addButton.dataset.busy = 'true';
    var restore = addLabel.innerHTML;
    addLabel.textContent = addButton.dataset.labelBusy || restore;

    /* Read the form on the NEXT tick, not now.

       Add-on apps commonly keep their selections in their own state and
       only write the `properties[…]` inputs into the form from their own
       submit listener. Every listener on this event runs during the same
       dispatch, so reading here can run before theirs and see nothing —
       which is why add-ons were reaching the cart empty. Deferring by a
       tick guarantees the dispatch has finished and their inputs exist.

       preventDefault above still stops the native navigation, and it
       does not stop the other listeners from running. */
    setTimeout(function () {
      submitLine(restore);
    }, 0);
  });

  function submitLine(restore) {
    var line = {
      id: variantInput.value,
      quantity: parseInt(qtyInput.value, 10) || 1
    };

    /* Everything on the line comes from the form, and nothing else.

       This handler used to build the line from the variant and quantity
       inputs alone, silently dropping every `properties[…]` field the
       add-on app renders. Add-ons were not merely missing from the cart
       display — they never reached the cart, so they were neither
       charged nor passed to the kitchen. */
    var lineProps = collectProperties();
    if (Object.keys(lineProps).length) line.properties = lineProps;

    // Subscription / selling plan, if the form offers one.
    var plan = form.querySelector('[name="selling_plan"]');
    if (plan && plan.value) line.selling_plan = plan.value;

    return fetch(routeRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [line] })
    })
      .then(function (res) {
        return res.json().then(function (body) {
          if (!res.ok) throw new Error(body.description || body.message || 'Could not add to cart');
          return body;
        });
      })
      .then(function () {
        addLabel.innerHTML = restore;
        if (window.IdeasCart) window.IdeasCart.refresh();
        document.dispatchEvent(
          new CustomEvent('ideas:item:added', {
            detail: { variantId: variantInput.value }
          })
        );
      })
      .catch(function (err) {
        console.warn('[ideas] add to cart failed', err);
        addLabel.innerHTML = restore;
      })
      .then(function () {
        addButton.dataset.busy = 'false';
      });
  }
})();
